/**
 * leadStatusUi.js — Shared status UI metadata for lead/treatment statuses.
 *
 * Single source of truth for Overview no-task cards and treatment status chip.
 * Consumers: OverView.jsx (status chip + no-task suggestion cards).
 *
 * Colors are aligned with LeadDetailConst.js → JOURNEY_STAGES.
 *
 * DO NOT import this in components outside the Overview/lead-detail family
 * without checking that the color palette matches expectations — other surfaces
 * (e.g. pipeline boards, list chips) use statusStyle() from styles/OverView.js
 * which has a deliberately different (lighter/muted) palette for contrast.
 */

// ─── Status map ──────────────────────────────────────────────────────────────

const STATUS_MAP = {
    NEW: {
        label: 'Enquiry',
        color: '#3b82f6',
        bgColor: 'rgba(59, 130, 246, 0.08)',
        borderColor: '#3b82f629',
        icon: 'mdi:question-mark-circle-outline',
        actionLabel: 'Follow up',
        noTaskMessage: 'No open tasks — use Follow up to contact this lead',
    },
    CONSULTATION_BOOKED: {
        label: 'Booked',
        color: '#f59e0b',
        bgColor: 'rgba(245, 158, 11, 0.08)',
        borderColor: '#f59e0b29',
        icon: 'mdi:calendar-clock',
        actionLabel: 'Record attendance',
        noTaskMessage: 'No open tasks — record attendance or manage the appointment',
    },
    CONSULTATION_ATTENDED: {
        label: 'Attended',
        color: '#8b5cf6',
        bgColor: 'rgba(139, 92, 246, 0.08)',
        borderColor: '#8b5cf629',
        icon: 'mdi:calendar-check',
        actionLabel: 'Follow up',
        noTaskMessage: 'No open tasks — use Follow up when ready',
    },
    FAILED_TO_ATTEND_CONSULTATION: {
        label: 'FTA',
        color: '#FF5630',
        bgColor: 'rgba(255, 86, 48, 0.08)',
        borderColor: '#FF563029',
        icon: 'bx:calendar-x',
        actionLabel: 'Follow up',
        noTaskMessage: 'No open tasks — use Follow up to rebook or archive',
    },
    TREATMENT_STARTED: {
        label: 'Treatment',
        color: '#00A76F',
        bgColor: 'rgba(0, 167, 111, 0.08)',
        borderColor: '#00A76F29',
        icon: 'codicon:debug-start',
        actionLabel: 'Treatment in progress',
        noTaskMessage: 'No open tasks — use Treatment in progress to manage the treatment',
    },
    CLOSED: {
        label: 'Complete',
        color: '#10b981',
        bgColor: 'rgba(16, 185, 129, 0.08)',
        borderColor: '#10b98129',
        icon: 'material-symbols:check-circle-outline',
        actionLabel: null,
        noTaskMessage: 'Treatment complete',
    },
};

// ─── Aliases — normalise short status strings from backend ───────────────────

const ALIASES = {
    ATTENDED: 'CONSULTATION_ATTENDED',
    BOOKED: 'CONSULTATION_BOOKED',
    FAILED_TO_ATTEND: 'FAILED_TO_ATTEND_CONSULTATION',
};

// ─── Fallback for unknown statuses ───────────────────────────────────────────

const FALLBACK = {
    label: 'Unknown',
    color: '#919EAB',
    bgColor: 'rgba(145, 158, 171, 0.08)',
    borderColor: '#919EAB29',
    icon: 'mdi:help-circle-outline',
    actionLabel: null,
    noTaskMessage: 'No open tasks',
};

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Returns UI metadata for a lead/treatment status.
 *
 * @param {string} status — e.g. 'NEW', 'CONSULTATION_ATTENDED', 'ATTENDED' (alias)
 * @returns {{
 *   status: string,
 *   label: string,
 *   color: string,
 *   bgColor: string,
 *   borderColor: string,
 *   icon: string,
 *   actionLabel: string|null,
 *   noTaskMessage: string,
 * }}
 */
export function getLeadStatusUi(status) {
    const resolved = ALIASES[status] || status;
    const config = STATUS_MAP[resolved] || FALLBACK;
    return { status: resolved, ...config };
}

/**
 * Returns a MUI sx object suitable for the treatment status chip in the Overview.
 * Produces vibrant pill styling aligned with JOURNEY_STAGES colors.
 *
 * Scoped to Overview/lead-detail use — do NOT use as a global replacement for
 * statusStyle() in styles/OverView.js without reviewing all consumers.
 *
 * @param {string} status
 * @returns {object} MUI sx object
 */
export function getLeadStatusChipSx(status) {
    const ui = getLeadStatusUi(status);
    return {
        fontSize: 14,
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        borderRadius: 1,
        p: '4px 12px 4px 4px!important',
        width: 'auto',
        minWidth: 'auto',
        maxWidth: 'fit-content',
        background: ui.bgColor,
        color: ui.color,
        border: 'none !important',
        textTransform: 'none !important',
        lineHeight: '18px',
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
    };
}
